import { useEffect, useMemo, useState } from 'react'
import { Modal } from '@trybeapp/ui.modal'
import { Formik, useField, useFormikContext } from 'formik'
import { Button } from '@trybeapp/ui.button'
import { string, object, date as yupDate } from 'yup'
import { TIMES_STAGE, useAddAppointmentOrderItemOverlay, useNewItem } from '../contexts'
import { Row, Col } from '@trybeapp/ui.grid'
import { ErrorProvider } from 'contexts/Error'
import { ErrorAlert } from 'components/ErrorAlert'
import { Box } from '@trybeapp/ui.box'
import { BackButton } from '..'
import { Input } from '@trybeapp/ui.input'
import { ConnectedField } from 'components/ConnectedField'
import { FormikRoomSelect } from 'components/fields/RoomSelect'
import { FormikPractitionerSelect } from 'components/fields/PractitionerSelect'
import { FormikDateInput } from 'components/DateInput'
import { useAppointmentType } from 'contexts/AppointmentType'
import { Alert } from '@trybeapp/ui.alert'
import { Hint } from 'ui/components/Hint'
import {
  useAllActiveBookingOrderItemDates,
  useAllActivePackageOrderItemDates,
  useOrderId,
} from 'screens/OrderDetails/components/Overview/hooks'
import moment from 'moment'
import { DateTime } from 'luxon'
import { useIsEditMode } from '../hooks'
import { SpinnerOverlay } from 'components/SpinnerOverlay'
import { AppointmentSlotSelection } from 'components/AppointmentSlotSelection'
import SectionHeading from 'components/SectionHeading'
import { trackEvent } from 'components/Segment'
import {
  EVENT_ADD_APPOINTMENT_STEP_COMPLETED,
  EVENT_APPOINTMENT_CREATED,
  EVENT_APPOINTMENT_UPDATED,
  EVENT_EDIT_APPOINTMENT_STEP_COMPLETED,
  EVENT_EDIT_APPOINTMENT_CANCELLED,
  EVENT_ADD_APPOINTMENT_CANCELLED,
} from 'constants/trackableEvents'
import { useCurrentSiteId } from 'contexts/SiteConfig'
import { useAddOrderItem, useEditOrderItem } from 'api/OrderItems'
import { useAppointmentTypes } from 'api/AppointmentTypes'
import { useAppointmentSlots } from 'api/AppointmentSlots'
import { useQueryParam } from 'use-query-params'
import { FormikEquipmentSelect } from 'components/fields/EquipmentSelect'
import { Label } from 'ui/components/Form/Label'
import { Checkbox } from 'ui/components/Form/Checkbox'
import { systemDateInSiteZone } from 'utilities/DateUtils/dateUtils'

export const TimesStage = () => {
  const { modal } = useAddAppointmentOrderItemOverlay()
  const orderId = useOrderId()
  const isEditMode = useIsEditMode()
  const {
    newItem: {
      id: itemId,
      guest_ids: guestIds,
      type_id: typeId,
      duration,
      date,
      start_time: time = '',
      room_id: roomId,
      practitioner_ids: practitionerIds = [],
      equipment_ids: equipmentIds = [],
    },
  } = useNewItem()

  const {
    mutate: mutateNewOrderItem,
    isLoading: isLoadingNewOrderItem,
    error: newOrderItemError,
  } = useAddOrderItem()
  const {
    mutate: mutateExistingOrderItem,
    isLoading: isLoadingExistingOrderItem,
    error: existingOrderItemError,
  } = useEditOrderItem()

  const initialValues = {
    date,
    time: time ? DateTime.fromJSDate(time).toFormat('HH:mm') : '',
    duration,
    room_id: roomId,
    practitioner_ids: practitionerIds,
    equipment_ids: equipmentIds,
    guest_ids: guestIds,
    offering_id: typeId,
    offering_type: 'appointment',
  }

  const validationSchema = object({
    date: yupDate().required('You must select a date'),
    time: string().required('You must select a time'),
  })

  const handleOnSubmit = (values) => {
    const { date, time, room_id: roomId, practitioner_ids: practitionerIds, ...newItem } = values
    const formattedDate = systemDateInSiteZone(date).startOf('day').toISODate()
    const timeParts = time.split(':')
    const formattedTime = systemDateInSiteZone(date)
      .set({ hour: timeParts[0], minute: timeParts[1] })
      .toISO({ suppressMilliseconds: true })
    const item = {
      ...newItem,
      date: formattedDate,
      time: formattedTime,
      practitioner_ids: practitionerIds ?? [],
      room_id: roomId ?? '',
    }

    if (isEditMode) {
      mutateExistingOrderItem(
        [
          orderId,
          itemId,
          item,
          {
            skipAvailabilityChecks: true,
          },
        ],
        { onSuccess: () => modal.hide() }
      )
      trackEvent(EVENT_APPOINTMENT_UPDATED, { orderId, orderItemId: itemId })
      trackEvent(EVENT_EDIT_APPOINTMENT_STEP_COMPLETED, {
        step: TIMES_STAGE,
        orderId,
        orderItemId: itemId,
      })
    } else {
      mutateNewOrderItem(
        [
          orderId,
          item,
          {
            skipAvailabilityChecks: true,
          },
        ],
        { onSuccess: () => modal.hide() }
      )

      trackEvent(EVENT_APPOINTMENT_CREATED, { orderId })
      trackEvent(EVENT_ADD_APPOINTMENT_STEP_COMPLETED, {
        step: TIMES_STAGE,
        orderId,
      })
    }
  }

  const closeModal = () => {
    modal.hide()

    if (isEditMode) {
      trackEvent(EVENT_EDIT_APPOINTMENT_CANCELLED, {
        step: TIMES_STAGE,
        orderId,
        orderItemId: itemId,
      })
    } else {
      trackEvent(EVENT_ADD_APPOINTMENT_CANCELLED, {
        step: TIMES_STAGE,
        orderId,
      })
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleOnSubmit}
      validationSchema={validationSchema}
      enableReinitialize={true}
    >
      <>
        <Modal.Header backgroundColor="light.800">Details</Modal.Header>
        <Modal.Content overflow="scroll">
          <ErrorProvider error={newOrderItemError || existingOrderItemError}>
            <ErrorAlert />
          </ErrorProvider>
          <Row minHeight={500} width={900}>
            <Col col={1 / 3}>
              <Box height={1}>
                <AppointmentItemForm />
              </Box>
            </Col>
            <Col col={2 / 3}>
              <Box position="relative" width={1} height={1} borderLeft={1} borderColor="light.500">
                <Box height={1} position="absolute" top={0} bottom={0} left={0} right={0} pl="xxl">
                  <SlotSelection />
                </Box>
              </Box>
            </Col>
          </Row>
        </Modal.Content>
        <Modal.Footer>
          <Box width={1} display="flex">
            <SubmitButton loading={isLoadingNewOrderItem || isLoadingExistingOrderItem} />
            <BackButton />
            <Box ml="auto">
              <Button variant="ghost" onClick={closeModal}>
                Close
              </Button>
            </Box>
          </Box>
        </Modal.Footer>
      </>
    </Formik>
  )
}

const useSlotParams = () => {
  const {
    values: { date: currentDate },
  } = useFormikContext()

  const {
    newItem: { id: itemId, guest_ids: guestIds = [] },
  } = useNewItem()

  return useMemo(() => {
    if (currentDate) {
      const dateObject = systemDateInSiteZone(currentDate).startOf('day')

      const params = {
        dateTimeFrom: dateObject.toISO({ suppressMilliseconds: true }),
        dateTimeTo: dateObject
          .endOf('day')
          .set({ millisecond: 0 })
          .toISO({ suppressMilliseconds: true }),
      }

      if (itemId) {
        params.excludeBasketItems = itemId
      }

      if (guestIds.length === 1) {
        params.guestId = guestIds[0]
      }

      return params
    }

    return {}
  }, [currentDate, itemId, guestIds])
}

const withEditingExistingItemCheck =
  (Component) =>
  ({ ...props }) => {
    const {
      newItem: { type_id: typeId },
    } = useNewItem()

    const siteId = useCurrentSiteId()

    const { data: { data: appointmentTypes = [] } = {}, isSuccess } = useAppointmentTypes(
      { perPage: -1, siteId },
      { refetchOnWindowFocus: false }
    )

    const unavailable = isSuccess && typeId && !appointmentTypes.find(({ id }) => id === typeId)

    if (!unavailable) return <Component {...props} />

    return (
      <Box my="sm">
        <SectionHeading>Time suggestions</SectionHeading>
        <Alert variant="error" mb="sm">
          <Box>
            It looks like the appointment type you are editing is no longer available. Update the
            times manually in the form on the left.
          </Box>
        </Alert>
      </Box>
    )
  }

const SlotSelection = withEditingExistingItemCheck(() => {
  const orderId = useOrderId()
  const { setFieldValue } = useFormikContext()
  const [includeUnavailable, setIncludeUnavailable] = useState(false)

  const {
    values: {
      date: currentDate,
      time,
      room_id: roomId,
      practitioner_ids: practitionerIds = [],
      equipment_ids: equipmentIds = [],
    },
  } = useFormikContext()

  const { min_guests: minGuests = 1, id: appointmentTypeId } = useAppointmentType()

  const params = useSlotParams()
  const {
    data: { data: slots = [] } = [],
    isSuccess: fetched,
    isLoading: fetching,
  } = useAppointmentSlots(
    { appointmentTypeId, basketId: orderId, ...params },
    {
      enabled: !!(params.dateTimeFrom && params.dateTimeTo),
    }
  )

  const visibleSlots = useMemo(() => {
    return slots.filter(({ quantity_available: available }) => includeUnavailable || available > 0)
  }, [slots, includeUnavailable])

  if (!currentDate) {
    return (
      <Box my="sm">
        <SlotsHeader
          includeUnavailable={includeUnavailable}
          setIncludeUnavailable={setIncludeUnavailable}
        />
        <Hint>Select a date to get applicable slots</Hint>
      </Box>
    )
  }

  if (fetched && visibleSlots.length === 0) {
    return (
      <Box my="sm">
        <SlotsHeader
          includeUnavailable={includeUnavailable}
          setIncludeUnavailable={setIncludeUnavailable}
        />
        <Hint>No slots available for the selected date</Hint>
      </Box>
    )
  }

  if (fetching) {
    return (
      <Box position="relative" minHeight={200}>
        <SpinnerOverlay backgroundColor="light.900" />
      </Box>
    )
  }

  return (
    <>
      <Box my="md">
        <SlotsHeader
          includeUnavailable={includeUnavailable}
          setIncludeUnavailable={setIncludeUnavailable}
        />
        <AppointmentSlotSelection
          time={time}
          practitionerIds={practitionerIds}
          roomId={roomId}
          equipmentIds={equipmentIds}
          options={visibleSlots}
          numPractitioners={minGuests}
          onTimeChange={(value) => {
            setFieldValue('time', value ? DateTime.fromJSDate(value).toFormat('HH:mm') : '')
          }}
          onPractitionerIdsChange={(value) => {
            setFieldValue('practitioner_ids', value)
          }}
          onRoomIdChange={(value) => {
            setFieldValue('room_id', value)
          }}
          onEquipmentIdsChange={(value) => {
            setFieldValue('equipment_ids', value)
          }}
        />
      </Box>
    </>
  )
})

const SlotsHeader = ({ includeUnavailable, setIncludeUnavailable }) => {
  return (
    <>
      <SectionHeading>Time suggestions</SectionHeading>
      <div className="mb-2">
        <Label className="flex gap-1">
          <Checkbox
            onChange={() => setIncludeUnavailable(!includeUnavailable)}
            checked={includeUnavailable}
          />
          <span>Show unavailable slots</span>
        </Label>
      </div>
    </>
  )
}

const SubmitButton = ({ loading }) => {
  const isEditMode = useIsEditMode()
  const { submitForm } = useFormikContext()

  return (
    <Button variant="primary" onClick={submitForm} loading={loading}>
      {isEditMode ? 'Edit' : 'Add'}
    </Button>
  )
}

const AppointmentItemForm = () => {
  const { name, equipment_ids: equipmentIds = [] } = useAppointmentType()
  const isEditMode = useIsEditMode()

  return (
    <>
      <Alert variant="info">
        You are {isEditMode ? 'editing' : 'adding'} <strong>{name}</strong>
      </Alert>
      <DateField />
      <TimeField />
      <RoomField />
      <PractitionerField />
      {equipmentIds.length > 0 && <EquipmentField />}
    </>
  )
}

const DateField = () => {
  return (
    <Box my="md">
      <ConnectedField label="Date" component={FormikDateInput} name="date" />
      <SuggestedDates />
    </Box>
  )
}

const TimeField = () => {
  return (
    <Box my="md">
      <ConnectedField label="Time" component={Input} name="time" type="time" step={300} />
    </Box>
  )
}

export const PractitionerField = () => {
  const { min_guests: minGuests = 1, max_guests: maxGuests = 1 } = useAppointmentType()
  const isMultiple = minGuests > 1
  const [, , { setValue }] = useField('practitioner_ids')

  const handleChange = (event) => {
    const value = typeof event.target.value === 'string' ? [event.target.value] : event.target.value

    setValue((value ?? []).slice(maxGuests * -1))
  }

  return (
    <Box my="md">
      <ConnectedField
        label={'Practitioner' + (isMultiple ? 's' : '')}
        component={FormikPractitionerSelect}
        includeUnassigned={true}
        name="practitioner_ids"
        isMultiple={isMultiple}
        onChange={handleChange}
      />
    </Box>
  )
}

const RoomField = () => {
  return (
    <Box my="md">
      <ConnectedField label="Room" component={FormikRoomSelect} name="room_id" />
    </Box>
  )
}

const EquipmentField = () => {
  return (
    <Box my="md">
      <ConnectedField
        label="Equipment"
        component={FormikEquipmentSelect}
        name="equipment_ids"
        isMultiple={true}
      />
    </Box>
  )
}

export const SuggestedDates = () => {
  const bookingDates = useAllActiveBookingOrderItemDates()
  const packageDates = useAllActivePackageOrderItemDates()
  const [date] = useQueryParam('date')
  const {
    setFieldValue,
    values: { date: currentDate },
  } = useFormikContext()

  useEffect(() => {
    if (!currentDate) {
      if (bookingDates.length > 0) {
        setFieldValue('date', moment(bookingDates[0]).toDate())
      } else if (packageDates.length > 0) {
        setFieldValue('date', moment(packageDates[0]).toDate())
      } else if (date) {
        setFieldValue('date', date)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate, date, bookingDates.length, packageDates.length])

  if (bookingDates.length > 0) {
    return (
      <>
        <Box my="sm">
          <Hint>Select a date from other items</Hint>
        </Box>
        <Row mb="md">
          {bookingDates.map((date) => (
            <Col col="auto" key={date}>
              <Button
                size="xs"
                variant="primary-info"
                onClick={() => setFieldValue('date', moment(date).toDate())}
              >
                {moment(date).format('DD/MM/YYYY')}
              </Button>
            </Col>
          ))}
        </Row>
      </>
    )
  }

  return null
}
